import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Car, Bike, Save, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function TravelRatesConfig({ user, onLogout }) {
  const [settings, setSettings] = useState({ per_km_rate_2w: 5, per_km_rate_4w: 10 });
  const [loading, setLoading] = useState(false);
  const [changed, setChanged] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/settings/travel-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        per_km_rate_2w: response.data.two_wheeler_rate_per_km || 5,
        per_km_rate_4w: response.data.four_wheeler_rate_per_km || 10,
        free_distance_km: response.data.free_distance_km || 15
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/settings/travel-rates`, {
        two_wheeler_rate_per_km: settings.per_km_rate_2w,
        four_wheeler_rate_per_km: settings.per_km_rate_4w,
        free_distance_km: settings.free_distance_km || 15
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Travel rates updated successfully!');
      setChanged(false);
      fetchSettings();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Travel Rates Configuration</h1>
          <p className="text-gray-600 mt-2">Configure per-kilometer reimbursement rates for travel claims</p>
        </div>

        {dataLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            <span className="ml-3 text-gray-500">Loading settings...</span>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl">
            <h2 className="text-xl font-bold mb-6" style={{color: '#2b3380'}}>Per-Kilometer Rates</h2>
            
            <div className="space-y-6">
              {/* 2-Wheeler Rate */}
              <div className="flex items-start gap-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Bike className="w-8 h-8 text-orange-600" />
                </div>
                <div className="flex-1">
                  <label className="block text-lg font-semibold text-gray-800 mb-2">
                    2-Wheeler Rate
                  </label>
                  <p className="text-sm text-gray-600 mb-3">
                    Reimbursement rate per kilometer for bike/scooter travel
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-medium text-gray-700">₹</span>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={settings.per_km_rate_2w}
                      onChange={(e) => {
                        setSettings({ ...settings, per_km_rate_2w: parseFloat(e.target.value) || 0 });
                        setChanged(true);
                      }}
                      className="w-32 px-4 py-2 border-2 border-gray-300 rounded-lg text-lg font-semibold focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                      data-testid="rate-2w-input"
                    />
                    <span className="text-lg font-medium text-gray-700">per km</span>
                  </div>
                </div>
              </div>

              {/* 4-Wheeler Rate */}
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Car className="w-8 h-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <label className="block text-lg font-semibold text-gray-800 mb-2">
                    4-Wheeler Rate
                  </label>
                  <p className="text-sm text-gray-600 mb-3">
                    Reimbursement rate per kilometer for car travel
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-medium text-gray-700">₹</span>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={settings.per_km_rate_4w}
                      onChange={(e) => {
                        setSettings({ ...settings, per_km_rate_4w: parseFloat(e.target.value) || 0 });
                        setChanged(true);
                      }}
                      className="w-32 px-4 py-2 border-2 border-gray-300 rounded-lg text-lg font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      data-testid="rate-4w-input"
                    />
                    <span className="text-lg font-medium text-gray-700">per km</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Rates Summary */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-700 mb-2">Current Rates Summary</h3>
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="text-gray-500">2-Wheeler:</span>
                  <span className="ml-2 font-semibold text-orange-600">₹{settings.per_km_rate_2w}/km</span>
                </div>
                <div>
                  <span className="text-gray-500">4-Wheeler:</span>
                  <span className="ml-2 font-semibold text-blue-600">₹{settings.per_km_rate_4w}/km</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {changed && (
              <div className="mt-6 flex gap-4">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-6 py-3 text-white rounded-lg hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#2b3380' }}
                  data-testid="save-rates-btn"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  onClick={() => { fetchSettings(); setChanged(false); }}
                  className="px-6 py-3 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition"
                  data-testid="reset-rates-btn"
                >
                  Reset
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TravelRatesConfig;
